import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UsersApiService, UserDirectoryRow } from '../../../services/users.api.service';
import { UserState } from '../../../states/user-state';
import {
  DialogConfirmComponent,
  DialogConfirmData,
} from '../../dialog/dialog-confirm/dialog-confirm.component';
import { userFacingErrorMessage } from '../../../utils/user-facing-error';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss', '../management.shared.scss'],
  standalone: false,
})
export class UsersComponent implements OnInit {
  users: UserDirectoryRow[] = [];
  total = 0;
  page = 1;
  pageSize = 25;
  loading = false;
  error = '';
  adminsOnly = false;
  removingUserId: string | null = null;
  searchControl = new FormControl('', { nonNullable: true });

  constructor(
    private usersApi: UsersApiService,
    private userState: UserState,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.page = 1;
        this.loadUsers();
      });
  }

  loadUsers(): void {
    this.loading = true;
    this.error = '';
    this.usersApi
      .listPaged({
        page: this.page,
        pageSize: this.pageSize,
        q: this.searchControl.value,
        adminsOnly: this.adminsOnly,
      })
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.users = res.items || [];
          this.total = res.total ?? 0;
        },
        error: (err) => {
          this.loading = false;
          console.error('Load users failed', err);
          this.error = userFacingErrorMessage(err, 'users');
        },
      });
  }

  toggleAdminsOnly(): void {
    this.adminsOnly = !this.adminsOnly;
    this.page = 1;
    this.loadUsers();
  }

  roleLabel(user: UserDirectoryRow): string {
    const names = (user.privilegeLevels || []).map((p) => p.name).filter(Boolean);
    return names.length ? names.join(', ') : '—';
  }

  adminLabel(user: UserDirectoryRow): string {
    return user.isAdmin ? 'Yes' : 'No';
  }

  validatedLabel(user: UserDirectoryRow): string {
    return user.validatedAt ? 'Yes' : 'No';
  }

  isCurrentUser(user: UserDirectoryRow): boolean {
    return user.id === this.userState.getUser()?.id;
  }

  confirmRemoveUser(user: UserDirectoryRow): void {
    if (this.removingUserId || this.isCurrentUser(user)) {
      return;
    }

    const adminNote = user.isAdmin
      ? ' This user is an administrator and will lose all access.'
      : '';
    const data: DialogConfirmData = {
      header: 'Remove user?',
      body: `Remove ${user.email} from the application? This cannot be undone.${adminNote}`,
      leftButton: 'Remove user',
      rightButton: 'Cancel',
      onLeftClick: () => this.removeUser(user),
    };

    this.dialog.open(DialogConfirmComponent, { width: '480px', data });
  }

  private removeUser(user: UserDirectoryRow): void {
    this.error = '';
    this.removingUserId = user.id;
    this.usersApi.deleteUser(user.id).subscribe({
      next: () => {
        this.removingUserId = null;
        this.users = this.users.filter((row) => row.id !== user.id);
        this.total = Math.max(0, this.total - 1);
      },
      error: (err) => {
        this.removingUserId = null;
        console.error('Remove user failed', err);
        this.error = 'Failed to remove user.';
      },
    });
  }
}
